import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { CheckCircle2, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PartnershipModal = ({ open, onOpenChange }) => {
  const [formData, setFormData] = useState({
    organizationName: '',
    contactName: '',
    email: '',
    phone: '',
    district: '',
    studentCount: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/partnership-request`, formData);
      console.log('Partnership request submitted:', response.data);
      setSubmitted(true);
      
      setTimeout(() => {
        setSubmitted(false);
        onOpenChange(false);
        setFormData({
          organizationName: '',
          contactName: '',
          email: '',
          phone: '',
          district: '',
          studentCount: '',
          message: ''
        });
      }, 2000);
    } catch (err) {
      console.error('Error submitting partnership request:', err);
      setError('Failed to submit. Please try again or call us at 209-200-9884.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        {!submitted ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-3xl font-bold text-[#0B0F14]">
                Start a Partnership
              </DialogTitle>
              <DialogDescription className="text-lg text-[#5F6B7A]">
                Let's discuss how we can support your students together.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6 mt-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="organizationName" className="text-[#0B0F14] font-semibold">
                    Organization Name *
                  </Label>
                  <Input
                    id="organizationName"
                    name="organizationName"
                    value={formData.organizationName}
                    onChange={handleChange}
                    required
                    className="mt-2"
                    placeholder="School District / SELPA"
                  />
                </div>
                
                <div>
                  <Label htmlFor="contactName" className="text-[#0B0F14] font-semibold">
                    Contact Name *
                  </Label>
                  <Input
                    id="contactName"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleChange}
                    required
                    className="mt-2"
                    placeholder="Your full name"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email" className="text-[#0B0F14] font-semibold">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-2"
                    placeholder="your@email.com"
                  />
                </div>
                
                <div>
                  <Label htmlFor="phone" className="text-[#0B0F14] font-semibold">
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="mt-2"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="district" className="text-[#0B0F14] font-semibold">
                    District/Location
                  </Label>
                  <Input
                    id="district"
                    name="district"
                    value={formData.district}
                    onChange={handleChange}
                    className="mt-2"
                    placeholder="City, State"
                  />
                </div>
                
                <div>
                  <Label htmlFor="studentCount" className="text-[#0B0F14] font-semibold">
                    Estimated Student Count
                  </Label>
                  <Input
                    id="studentCount"
                    name="studentCount"
                    value={formData.studentCount}
                    onChange={handleChange}
                    className="mt-2"
                    placeholder="Approximate number"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="message" className="text-[#0B0F14] font-semibold">
                  Tell Us About Your Needs
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className="mt-2"
                  rows={4}
                  placeholder="Share details about your transportation needs, student population, or any specific requirements..."
                />
              </div>

              <div className="space-y-2 text-sm">
                {error && (
                  <div className="p-4 rounded-xl bg-red-50 border border-red-200 mb-4">
                    <p className="text-red-600 font-semibold">{error}</p>
                  </div>
                )}
              </div>

              <div className="flex gap-4">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-gradient-to-r from-[#0077BE] to-[#00B4D8] text-white hover:shadow-xl py-6 text-lg font-semibold rounded-xl"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Partnership Request'
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  className="px-8 py-6 rounded-xl"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </>
        ) : (
          <div className="py-12 text-center">
            <div className="w-20 h-20 rounded-full bg-[#2ECC71]/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-12 h-12 text-[#2ECC71]" />
            </div>
            <h3 className="text-2xl font-bold text-[#0B0F14] mb-3">
              Request Submitted!
            </h3>
            <p className="text-lg text-[#5F6B7A]">
              Thank you for your interest. Our team will contact you within 24 hours.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PartnershipModal;
